import { FC } from 'react';

import { Formik } from 'formik';
import { isStrictEqual } from 'remeda';
import { BaseDateTimePicker, BaseInput, BaseSelect, FormBodyContainer } from 'wbs-components-lib';

import {
  VIP_TASK_ITEM_PARTNER_ACTION_TYPES,
  VIP_TASK_ITEM_PLATFORM_ACTION_TYPES,
  VipTaskItemTypesEnum,
  VipTaskItemUserIdentifierTypesEnum,
  VipTaskTypesEnum,
} from '@shared/constants';
import { prepareItemsForBaseSelect } from '@shared/utils';

import { VipTaskItemFormProps } from './types';
import { vipTaskItemValidationSchema } from './validationSchema';

export const VipTaskItemForm: FC<VipTaskItemFormProps> = ({
  initialValues,
  onSubmit,
  isSubmitting = false,
  submitButtonCaption = 'Create',
  vipTaskType,
}) => {
  const selectUserIdentifierItems = prepareItemsForBaseSelect(Object.values(VipTaskItemUserIdentifierTypesEnum));

  const selectTaskItemTypeItems = prepareItemsForBaseSelect(
    vipTaskType === VipTaskTypesEnum.PARTNER ? VIP_TASK_ITEM_PARTNER_ACTION_TYPES : VIP_TASK_ITEM_PLATFORM_ACTION_TYPES
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={vipTaskItemValidationSchema}
      enableReinitialize
    >
      {({ submitForm, values, handleChange, handleBlur, setFieldValue, errors, touched }) => {
        const {
          title: titleValue,
          description: descriptionValue,
          type: typeValue,
          reward: rewardValue,
          order: orderValue,
          userIdentifierType: userIdentifierTypeValue,
          completedTasksCount: completedTasksCountValue,
          joinedTournamentsCount: joinedTournamentsCountValue,
          joinedTournamentsStartDate: joinedTournamentsStartDateValue,
        } = values;
        const {
          title: titleError,
          description: descriptionError,
          type: typeError,
          reward: rewardError,
          order: orderError,
          userIdentifierType: userIdentifierTypeError,
          completedTasksCount: completedTasksCountError,
          joinedTournamentsCount: joinedTournamentsCountError,
          joinedTournamentsStartDate: joinedTournamentsStartDateError,
        } = errors;
        const {
          title: isTitleTouched,
          description: isDescriptionTouched,
          type: isTypeTouched,
          reward: isRewardTouched,
          order: isOrderTouched,
          userIdentifierType: isUserIdentifierTypeTouched,
          completedTasksCount: isCompletedTasksCountTouched,
          joinedTournamentsCount: isJoinedTournamentsCountTouched,
          joinedTournamentsStartDate: isJoinedTournamentsStartDateTouched,
        } = touched;

        const onCasinoSubmit = async () => {
          await submitForm();
        };

        const isTitleError = isTitleTouched && !!titleError;
        const isDescriptionError = isDescriptionTouched && !!descriptionError;
        const isTypeError = isTypeTouched && !!typeError;
        const isRewardError = isRewardTouched && !!rewardError;
        const isOrderError = isOrderTouched && !!orderError;
        const isUserIdentifierTypeError = isUserIdentifierTypeTouched && !!userIdentifierTypeError;
        const isCompletedTasksCountError = isCompletedTasksCountTouched && !!completedTasksCountError;
        const isJoinedTournamentsCountError = isJoinedTournamentsCountTouched && !!joinedTournamentsCountError;
        const isJoinedTournamentsStartDateError =
          isJoinedTournamentsStartDateTouched && !!joinedTournamentsStartDateError;

        const isSubmitButtonDisabled = isStrictEqual(values, initialValues);

        const renderTaskItemParamsByType = () => {
          switch (typeValue) {
            case VipTaskItemTypesEnum.VALIDATE_PARTNER_ACTION: {
              return (
                <BaseSelect
                  // @ts-ignore
                  id="userIdentifierType"
                  name="userIdentifierType"
                  errorText={userIdentifierTypeError}
                  isError={isUserIdentifierTypeError}
                  onBlur={handleBlur}
                  items={selectUserIdentifierItems}
                  handleChange={(value: string) => setFieldValue('userIdentifierType', value)}
                  value={userIdentifierTypeValue || VipTaskItemUserIdentifierTypesEnum.EMAIL}
                  caption="User Identifier Type"
                  label="Select user identifier type"
                />
              );
            }
            case VipTaskItemTypesEnum.COMPLETE_TASKS: {
              return (
                <BaseInput
                  helperText={isCompletedTasksCountError && completedTasksCountError}
                  label="Enter completed tasks count"
                  id="completedTasksCount"
                  name="completedTasksCount"
                  caption="Completed tasks count"
                  type="number"
                  value={completedTasksCountValue}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={isCompletedTasksCountError}
                />
              );
            }
            case VipTaskItemTypesEnum.JOIN_TOURNAMENTS: {
              return (
                <>
                  <BaseInput
                    helperText={isJoinedTournamentsCountError && joinedTournamentsCountError}
                    label="Enter joined tournaments count"
                    id="joinedTournamentsCount"
                    name="joinedTournamentsCount"
                    caption="Completed tasks count"
                    type="number"
                    value={joinedTournamentsCountValue}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={isJoinedTournamentsCountError}
                  />
                  <BaseDateTimePicker
                    id="joinedTournamentsStartDate"
                    name="joinedTournamentsStartDate"
                    errorMessage={
                      isJoinedTournamentsStartDateError ? (joinedTournamentsStartDateError as string) : undefined
                    }
                    caption="Start Date"
                    onBlur={handleBlur}
                    value={joinedTournamentsStartDateValue || null}
                    onChange={(value) => setFieldValue('joinedTournamentsStartDate', value)}
                  />
                </>
              );
            }
            default: {
              return null;
            }
          }
        };

        return (
          <FormBodyContainer
            isSubmitting={isSubmitting}
            onSubmit={onCasinoSubmit}
            submitButtonCaption={submitButtonCaption}
            isSubmitButtonDisabled={isSubmitButtonDisabled}
          >
            <BaseInput
              helperText={isTitleError && titleError}
              label="Enter title"
              id="title"
              name="title"
              caption="Title"
              value={titleValue}
              onBlur={handleBlur}
              onChange={handleChange}
              error={isTitleError}
            />
            <BaseInput
              helperText={isDescriptionError && descriptionError}
              label="Enter description"
              id="description"
              name="description"
              caption="Description"
              value={descriptionValue}
              onBlur={handleBlur}
              onChange={handleChange}
              error={isDescriptionError}
            />
            <BaseSelect
              // @ts-ignore
              id="type"
              name="type"
              errorText={typeError}
              isError={isTypeError}
              onBlur={handleBlur}
              items={selectTaskItemTypeItems}
              handleChange={(value: string) => setFieldValue('type', value)}
              value={typeValue}
              caption="Task Item Type"
              label="Select task item type"
            />
            {renderTaskItemParamsByType()}
            <BaseInput
              helperText={isRewardError && rewardError}
              label="Enter VP"
              id="reward"
              name="reward"
              caption="VIP points"
              type="number"
              value={rewardValue}
              onBlur={handleBlur}
              onChange={handleChange}
              error={isRewardError}
            />
            <BaseInput
              id="order"
              name="order"
              type="number"
              helperText={isOrderError && orderError}
              error={isOrderError}
              value={orderValue}
              label="Enter order"
              captionColor="#000000"
              onBlur={handleBlur}
              onChange={handleChange}
            />
          </FormBodyContainer>
        );
      }}
    </Formik>
  );
};
